import { ContainerPadding, Text, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import { Alert, CheckmarkCircle, CloseCircle } from '@volkswagen-onehub/icons-core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CodigoPostalInput } from '../../../components/inputs-group/inputs/codigo-postal-input';
import { ComentarioInput } from '../../../components/inputs-group/inputs/comentario-input';
import { CtasRadio } from '../../../components/inputs-group/inputs/core/ctas-radio';
import { OptionCard } from '../../../components/inputs-group/inputs/core/option-card';
import { DniInput } from '../../../components/inputs-group/inputs/dni-input';
import { EmailInput } from '../../../components/inputs-group/inputs/email-input';
import { LettersInput } from '../../../components/inputs-group/inputs/letters-input';
import { OfertaOptionCard } from '../../../components/inputs-group/inputs/oferta-option-card';
import { TelefonoInput } from '../../../components/inputs-group/inputs/telefono-input';
import { TratoNombreInput } from '../../../components/inputs-group/inputs/trato-nombre-input';
import { VehicleSelect } from '../../../components/inputs-group/inputs/vehicle-select';
import { CardsGroup } from '../../../components/screen/cards-group';
import { InputsGroup } from '../../../components/screen/inputs-group';
import { Section } from '../../../components/screen/section';
import { FIELD_NAME } from '../../../constants/form-constants';
import { useApp } from '../../../contexts/app-context';
import { ndpsEntity } from '../../../entities/ndps-entity';
import { personEntity } from '../../../entities/person-entity';
import { formService } from '../../../form-service';
import { isComerciales } from '../../../helpers/carline-helpers';
import { externalContentManager } from '../../../service/external-content';
import { getLeadVentaProductData } from '../lead-venta-product-data';
import { CallCenterDealersRadio } from './call-center-dealers-radio';
import { CallCenterEnvioSection } from './call-center-envio-section';

const CodigoPostalInputCondicional = () => {
	const [required, setRequired] = useState(() => formService.getFieldValue('estadoLlamada') === 'Argumentado');

	useEffect(() => {
		return formService.subscribeToField('estadoLlamada', {
			onChange: (value) => setRequired(value === 'Argumentado'),
		});
	}, []);

	return <CodigoPostalInput required={required} />;
};

export const CallCenterForm = () => {
	const { setAppLoading, setAppError, setAppErrorLog } = useApp();

	const isArgumentadoRef = useRef(formService.getFieldValue('estadoLlamada') === 'Argumentado');
	const [lssi, setLssi] = useState('');
	const [tmk, setTmk] = useState('');
	const [carlineCode, setCarlineCode] = useState<string | number | undefined>();

	const getFormData = useCallback(() => {
		const ndpsToken =
			isComerciales() || formService.getFieldValue('brand') == 'N'
				? 'LCV_DDB_FRM_CALL_ME_NOW'
				: 'VW_DDB_FRM_LEM_REC_LEM';
		ndpsEntity.setToken(ndpsToken);

		const isArgumentado = isArgumentadoRef.current;
		return {
			'headers[FK_SYSTEM]': ndpsToken,
			'headers[DPS_TOKEN]': ndpsToken,
			'headers[FORM_MKT_CHANNEL]': 'Call Center',
			'headers[FORM_DETAILS]': formService.getFieldValue('estadoLlamada'),

			'flags[PERSON_RGPD_ADAPTED]': true,
			'flags[FLAG_IS_MYSTERY_LEAD]': false,
			'flags[FLAG_RELACION_MARCA_SIN_IC]': false,
			'flags[FLAG_SF_INTEGRATION]': true,
			'flags[FLAG_IS_COMPANY]': isComerciales() ? false : undefined,
			'flags[FLAG_ALLOWS_DATA_ENRICHMENT]': isComerciales() ? undefined : false,

			'fields[PERSON_TREATMENT]': isComerciales() ? personEntity.treatment : undefined,
			'fields[PERSON_NAME]': personEntity.name,
			'fields[PERSON_SURNAME_1]': personEntity.surname,
			'fields[PERSON_SURNAME_2]': personEntity.secondSurname,
			'fields[PERSON_EMAIL]': personEntity.email,
			'fields[PERSON_PHONE_PREFIX]': personEntity.phonePrefix,
			'fields[PERSON_PHONE_MOBILE]': personEntity.phone,
			'fields[PERSON_PHONE_MOBILE_FULL]': personEntity.phonePrefix + personEntity.phone,
			'fields[PERSON_POSTAL_CP]': personEntity.postalCode,
			'fields[PERSON_DNI_NIF]': personEntity.dni,
			'fields[COMMENTS_PERSON]': formService.getFieldValue(FIELD_NAME.COMMENT),

			// PRODUCT DATA (cada data sobreescribe valores del anterior)
			...ndpsEntity.dealerData,
			...ndpsEntity.vehicleData,

			'fields[VEH_INTEREST_LINK]': formService.getFieldValue('ref'),
			'fields[SF_LEAD_SALES_INITIAL_ACTION]': isArgumentado ? 'Direct to Sales Assistant' : 'Prospect',
		};
	}, []);

	useEffect(() => {
		return formService.subscribeToField('estadoLlamada', {
			onChange: (value) => {
				isArgumentadoRef.current = value === 'Argumentado';
			},
		});
	}, []);

	useEffect(() => {
		setAppLoading(true);

		setTimeout(() => {
			const preload = externalContentManager.token;
			if (!preload || !preload.brand) {
				setAppError(true);
				setAppErrorLog('Faltan datos en la url o no es correcta');
				return;
			}

			formService.setFieldValue('brand', preload.brand);
			formService.setFieldValue(FIELD_NAME.NAME, preload.name);
			formService.setFieldValue(FIELD_NAME.PHONE, preload.cellphone);
			formService.setFieldValue(FIELD_NAME.PHONE_PREFIX, preload.prefix);
			formService.setFieldValue(FIELD_NAME.RGPD_COMUNICACIONES_COMERCIALES, preload.lssi);
			setLssi(preload.lssi);
			formService.setFieldValue(FIELD_NAME.RGPD_PERFILADO, preload.tmk);
			setTmk(preload.tmk);
			formService.setFieldValue('ref', preload?.ref);

			setCarlineCode(preload?.carlineCode);

			getLeadVentaProductData().then(() => {
				setAppLoading(false);
			});
		}, 0);
	}, []);

	const optionsOrigenLead = [
		{ text: 'Servicio habitual', value: 'Servicio habitual' },
		{ text: 'ID.4 Renting Online', value: 'ID.4 Renting Online' },
		{ text: 'No enviado', value: 'No enviado' },
	];
	const optionsTipoLead = [
		{ text: 'Test Drive', value: 'Test Drive' },
		{ text: 'Más Información', value: 'Más Información' },
		{ text: 'Presupuesto', value: 'Presupuesto' },
		{ text: 'Cita Previa', value: 'Cita Previa' },
	];
	const optionsEstadoLlamada = [
		{ text: 'Argumentado', value: 'Argumentado' },
		{ text: 'No argumentado', value: 'No argumentado' },
		{ text: 'Solo quiere info del Call Center', value: 'Solo quiere info del Call Center' },
		{ text: 'Ilocalizable', value: 'Ilocalizable' },
	];

	const Form = () => (
		<>
			<Section paddingBottom={ContainerPadding.static515}>
				<Text bold appearance={TokenTextAppearance.headline500}>
					Call me now
				</Text>
			</Section>
			{isComerciales() ? (
				<Section paddingBottom={ContainerPadding.static515}>
					<Text bold appearance={TokenTextAppearance.headline200}>
						Estado de la llamada
					</Text>
					<CtasRadio name="estadoLlamada" options={optionsEstadoLlamada} required />
				</Section>
			) : (
				<>
					<Section paddingBottom={ContainerPadding.static515}>
						<Text bold appearance={TokenTextAppearance.headline200}>
							¿Origen del lead?
						</Text>
						<CtasRadio name="origenLead" options={optionsOrigenLead} required />
					</Section>
					<Section paddingBottom={ContainerPadding.static515}>
						<Text bold appearance={TokenTextAppearance.headline200}>
							¿Que tipo de leads es?
						</Text>
						<CtasRadio name="antiguedad" options={optionsTipoLead} required />
					</Section>
				</>
			)}
			<Section paddingBottom={ContainerPadding.static515}>
				<Text bold appearance={TokenTextAppearance.headline200}>
					Datos personales
				</Text>
				<InputsGroup>
					{isComerciales() ? <TratoNombreInput /> : <LettersInput name={FIELD_NAME.NAME} required />}
					<LettersInput name={FIELD_NAME.SURNAME_1} label="Primer apellido" required />
					<LettersInput name={FIELD_NAME.SURNAME_2} label="Segundo apellido" required />
					{isComerciales() && <DniInput required />}
					{isComerciales() && <CodigoPostalInputCondicional />}
				</InputsGroup>
			</Section>

			<Section paddingBottom={ContainerPadding.static515}>
				<Text bold appearance={TokenTextAppearance.headline200}>
					Datos de contacto
				</Text>
				<InputsGroup>
					<TelefonoInput required />
					<EmailInput required />
				</InputsGroup>
				<CardsGroup>
					<OptionCard
						selectable={false}
						icon={lssi == 'PERMITE' ? <CheckmarkCircle /> : lssi == 'NO PERMITE' ? <Alert /> : <CloseCircle />}
					>
						Comunicaciones comerciales: <Text bold>{lssi}</Text>
					</OptionCard>
					<OptionCard
						selectable={false}
						icon={tmk == 'PERMITE' ? <CheckmarkCircle /> : tmk == 'NO PERMITE' ? <Alert /> : <CloseCircle />}
					>
						Perfilado: <Text bold>{tmk}</Text>
					</OptionCard>
				</CardsGroup>
			</Section>

			{!isComerciales() && (
				<Section paddingBottom={ContainerPadding.static515}>
					<Text bold appearance={TokenTextAppearance.headline200}>
						¿Quiere saber algo en concreto?
					</Text>
					<InputsGroup>
						<ComentarioInput />
					</InputsGroup>
				</Section>
			)}

			<Section paddingBottom={ContainerPadding.static515}>
				<Text bold appearance={TokenTextAppearance.headline200}>
					Datos {formService.getFieldValue(FIELD_NAME.OFERTA) ? 'de la oferta' : 'del vehículo'}
				</Text>

				{formService.getFieldValue(FIELD_NAME.OFERTA) ? (
					<CardsGroup>
						<OfertaOptionCard oferta={formService.getFieldValue(FIELD_NAME.OFERTA)} />
					</CardsGroup>
				) : (
					<InputsGroup>
						<VehicleSelect required carlineCode={carlineCode} />
					</InputsGroup>
				)}
			</Section>

			<Section paddingBottom={ContainerPadding.static515}>
				<Text bold appearance={TokenTextAppearance.headline200}>
					Datos de la instalación
				</Text>
				<CallCenterDealersRadio />
			</Section>

			<CallCenterEnvioSection formData={getFormData} errorMessage={undefined} />
		</>
	);

	return Form();
};
